import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Search, Trash2, KeyRound, Plus, ChevronDown, ChevronRight, Smartphone } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import CreateDashboardPopup from './CreateDashboardPopup';
import ConfirmDeleteAdminPopup from './ConfirmDeleteAdminPopup';
import AddUserPopup from './addUserPopup';
import { buildPasswordPolicyContext } from '@/_helpers/passwordPolicy';

const ITEMS_PER_PAGE = 12;

const ManageUserAdmin = ({ closePopup }) => {
    const [deletePopup, setDeletePopup] = useState(false);
    const [createDashboardPopup, setCreateDashboardPopup] = useState(false);
    const [resendPasswordPopup, setResendPasswordPopup] = useState(false);
    const [addUserPopup, setAddUserPopup] = useState(false);
    const [userList, setUserList] = useState([]);
    const [userListTemp, setUserListTemp] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchText, setSearchText] = useState('');
    const [userName, setUserName] = useState('');
    const [userEmail, setUserEmail] = useState('');
    const [userId, setUserId] = useState('');
    const [wholesalerName, setWholesalerName] = useState('');
    const [sortBy, setSortBy] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [expandedRows, setExpandedRows] = useState({});
    const [opStatus, setOpStatus] = useState(false);
    const [isMobile, setIsMobile] = useState(false);

    // Detect mobile screen
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };
        
        checkMobile();
        window.addEventListener('resize', checkMobile);
        
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const userdata = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('user'));
        } catch {
            return null;
        }
    }, []);

    const adminResendPasswordPolicyContext = useMemo(
        () => buildPasswordPolicyContext({ email: userEmail, username: userName, fullName: userName || '' }),
        [userEmail, userName]
    );

    const handleResponse = useCallback((response) => {
        return response.text().then(text => {
            try {
                const data = text && JSON.parse(text);
                return data;
            } catch {
                return [];
            }
        });
    }, []);

    const getDistributorList = useCallback((roleName, roleType) => {
        const requestOptions = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-Browser-Url': window.location.href },
            body: JSON.stringify({ roleName, roleType })
        };

        setLoading(true);
        fetch(`/api/admin/Wh_DashList`, requestOptions)
            .then(handleResponse)
            .then(apidata => {
                setUserListTemp(apidata || []);
                
                let filteredList = apidata || [];
                if (sortBy === 'withdashboard') {
                    filteredList = apidata.filter(e => e.dashid !== "0");
                } else if (sortBy === 'withoutdashboard') {
                    filteredList = apidata.filter(e => e.dashid === "0");
                }
                
                setUserList(filteredList);
                setLoading(false);
                setCurrentPage(1);
            })
            .catch(error => {
                setLoading(false);
            });
    }, [handleResponse, sortBy]);

    useEffect(() => {
        if (userdata) {
            getDistributorList(userdata.paramsname, userdata.userrole);
        }
    }, [userdata, getDistributorList]);

    const handleSearchFilter = useCallback((e) => {
        const val = e.target.value.toLowerCase();
        setSearchText(val);
        
        if (val.length === 0) {
            if (sortBy === 'withdashboard') {
                setUserList(userListTemp.filter(e => e.dashid !== "0"));
            } else if (sortBy === 'withoutdashboard') {
                setUserList(userListTemp.filter(e => e.dashid === "0"));
            } else {
                setUserList(userListTemp);
            }
        } else {
            const filtered = userListTemp.filter(element => {
                const whNameMatch = element.whname?.toLowerCase().includes(val);
                const distMatch = element.dist?.some(d => 
                    d.username?.toLowerCase().includes(val) || 
                    d.useremail?.toLowerCase().includes(val)
                );
                return whNameMatch || distMatch;
            });
            
            if (sortBy === 'withdashboard') {
                setUserList(filtered.filter(e => e.dashid !== "0"));
            } else if (sortBy === 'withoutdashboard') {
                setUserList(filtered.filter(e => e.dashid === "0"));
            } else {
                setUserList(filtered);
            }
        }
        setCurrentPage(1);
    }, [userListTemp, sortBy]);

    const handleSortChange = useCallback((value) => {
        setSortBy(value);
        let filtered = [...userListTemp];
        
        if (value === 'withdashboard') {
            filtered = userListTemp.filter(e => e.dashid !== "0");
        } else if (value === 'withoutdashboard') {
            filtered = userListTemp.filter(e => e.dashid === "0");
        }
        
        setUserList(filtered);
        setCurrentPage(1);
    }, [userListTemp]);

    const toggleRowExpansion = useCallback((whname) => {
        setExpandedRows(prev => ({
            ...prev,
            [whname]: !prev[whname]
        }));
    }, []);

    const handleDeletePopup = useCallback((name, email, id, whname) => {
        setUserName(name);
        setUserEmail(email);
        setUserId(id);
        setWholesalerName(whname);
        setDeletePopup(true);
    }, []);

    const handleResendPasswordPopup = useCallback((name, email, id, whname) => {
        setUserName(name);
        setUserEmail(email);
        setUserId(id);
        setWholesalerName(whname);
        setResendPasswordPopup(true);
    }, []);

    const handleCreateDashboardPopup = useCallback((whname) => {
        setWholesalerName(whname);
        setCreateDashboardPopup(true);
    }, []);

    const handleAddUser = useCallback((whname) => {
        setWholesalerName(whname);
        setAddUserPopup(true);
    }, []);

    const removeDashboardAccount = useCallback(() => {
        const requestOptions = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                Wholesaler_Name: wholesalerName,
                User_Id: userId,
                User_Email: userEmail
            })
        };

        fetch(`/api/admin/RemoveDashboardAcc`, requestOptions)
            .then(handleResponse)
            .then(() => {
                setDeletePopup(false);
                setOpStatus(true);
                if (userdata) {
                    getDistributorList(userdata.paramsname, userdata.userrole);
                }
                setTimeout(() => setOpStatus(false), 3000);
            })
            .catch(error => {
            });
    }, [wholesalerName, userId, userEmail, handleResponse, userdata, getDistributorList]);

    const handleSubmit = useCallback((newPassword) => {
        const requestOptions = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                UserEmail: userEmail,
                password: newPassword
            })
        };

        fetch(`/api/user/ChangePWD_From_Admin`, requestOptions)
            .then(handleResponse)
            .then(() => {
                setResendPasswordPopup(false);
                setOpStatus(true);
                setTimeout(() => setOpStatus(false), 3000);
            })
            .catch(error => {
            });
    }, [userEmail, handleResponse]);

    const handleConfirmCreateDashboard = useCallback(() => {
        const requestOptions = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                Wholesaler_Name: wholesalerName
            })
        };

        fetch(`/api/admin/CreateBlankDash`, requestOptions)
            .then(handleResponse)
            .then(() => {
                setCreateDashboardPopup(false);
                setOpStatus(true);
                if (userdata) {
                    getDistributorList(userdata.paramsname, userdata.userrole);
                }
                setTimeout(() => setOpStatus(false), 3000);
            })
            .catch(error => {
            });
    }, [wholesalerName, handleResponse, userdata, getDistributorList]);

    const handleAddUserSubmit = useCallback((userData) => {
        const requestOptions = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                Wholesaler_Name: wholesalerName,
                ...userData
            })
        };

        fetch(`/api/admin/AddUserToDash`, requestOptions)
            .then(handleResponse)
            .then(() => {
                setAddUserPopup(false);
                setOpStatus(true);
                if (userdata) {
                    getDistributorList(userdata.paramsname, userdata.userrole);
                }
                setTimeout(() => setOpStatus(false), 3000);
            })
            .catch(error => {
            });
    }, [wholesalerName, handleResponse, userdata, getDistributorList]);

    // Pagination calculations
    const totalPages = Math.ceil(userList.length / ITEMS_PER_PAGE);
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    const paginatedList = userList.slice(startIndex, endIndex);

    const handlePageChange = useCallback((page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    }, [totalPages]);

    const pageNumbers = useMemo(() => {
        const pages = [];
        const maxVisiblePages = 6;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        if (endPage - startPage < maxVisiblePages - 1) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }
        return pages;
    }, [currentPage, totalPages]);

    return (
        <Dialog open={true} onOpenChange={closePopup}>
            <DialogContent className="max-w-7xl">
                <DialogHeader>
                    <DialogTitle>Manage Users</DialogTitle>
                </DialogHeader>

                {loading ? (
                    <div className="flex justify-center items-center py-8">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    </div>
                ) : isMobile ? (
                    <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                        <Smartphone className="h-16 w-16 text-muted-foreground mb-4" />
                        <h3 className="text-lg font-semibold text-foreground mb-2">
                            Content Not Available
                        </h3>
                        <p className="text-sm text-muted-foreground">
                            Content not available for mobile screen. Please use a desktop or tablet device.
                        </p>
                    </div>
                ) : (
                <>
                {opStatus && (
                    <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded">
                        Operation completed successfully!
                    </div>
                )}

                <div className="space-y-4">
                    <div className="flex items-center gap-4">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                            <Input
                                type="text"
                                placeholder="Search by name or email"
                                value={searchText}
                                onChange={handleSearchFilter}
                                className="searchFilter pl-10"
                            />
                        </div>

                        <Tabs value={sortBy} onValueChange={handleSortChange} className="w-auto">
                            <TabsList>
                                <TabsTrigger value="all">All</TabsTrigger>
                                <TabsTrigger value="withdashboard">With Dashboard</TabsTrigger>
                                <TabsTrigger value="withoutdashboard">Without Dashboard</TabsTrigger>
                            </TabsList>
                        </Tabs>
                    </div>

                    <div className="border rounded-lg">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-12">#</TableHead>
                                    <TableHead>Wholesaler Name</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead className="text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {paginatedList.length > 0 ? (
                                    paginatedList.map((item, index) => {
                                        const isExpanded = expandedRows[item.whname];
                                        const hasUsers = item.dist && item.dist.length > 0;
                                        const noDashboard = item.dashstatus === "0" || item.dashstatus === "No Dashboard";
                                        const dashboardExistsNoUser = item.dashstatus === "Dashboard exits without user";

                                        return (
                                            <React.Fragment key={item.whname}>
                                                <TableRow className="hover:bg-muted/50">
                                                    <TableCell>{startIndex + index + 1}</TableCell>
                                                    <TableCell className="font-medium">
                                                        <div className="flex items-center gap-2">
                                                            {(hasUsers || dashboardExistsNoUser) && (
                                                <Button
                                                    variant="ghost"
                                                    onClick={() => toggleRowExpansion(item.whname)}
                                                    className="h-auto w-auto p-1 hover:bg-muted rounded"
                                                >
                                                                    {isExpanded ? (
                                                                        <ChevronDown className="h-4 w-4" />
                                                                    ) : (
                                                                        <ChevronRight className="h-4 w-4" />
                                                                    )}
                                                                </Button>
                                                            )}
                                                            {item.whname}
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>
                                                        {noDashboard ? (
                                                            <Badge variant="secondary">No Dashboard</Badge>
                                                        ) : dashboardExistsNoUser ? (
                                                            <Badge variant="secondary">Dashboard Without User</Badge>
                                                        ) : (
                                                            <Badge variant="default">{item.dist?.length || 0} User(s)</Badge>
                                                        )}
                                                    </TableCell>
                                                    <TableCell className="text-right">
                                                        {noDashboard && (
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => handleCreateDashboardPopup(item.whname)}
                                                            >
                                                                <Plus className="h-4 w-4 mr-2" />
                                                                Create Dashboard
                                                            </Button>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                                
                                                {isExpanded && (hasUsers || dashboardExistsNoUser) && (
                                                    <>
                                                        {hasUsers && item.dist.map((user, userIndex) => (
                                                            <TableRow key={`${item.whname}-${userIndex}`} className="bg-muted/30">
                                                                <TableCell></TableCell>
                                                                <TableCell className="pl-12">{user.username}</TableCell>
                                                                <TableCell>{user.useremail}</TableCell>
                                                                <TableCell className="text-right">
                                                                    <div className="flex justify-end gap-2">
                                                                        <Button
                                                                            variant="ghost"
                                                                            size="sm"
                                                                            onClick={() => handleResendPasswordPopup(
                                                                                user.username,
                                                                                user.useremail,
                                                                                user.userid,
                                                                                item.whname
                                                                            )}
                                                                        >
                                                                            <KeyRound className="h-4 w-4" />
                                                                        </Button>
                                                                        <Button
                                                                            variant="ghost"
                                                                            size="sm"
                                                                            onClick={() => handleDeletePopup(
                                                                                user.username,
                                                                                user.useremail,
                                                                                user.userid,
                                                                                item.whname
                                                                            )}
                                                                        >
                                                                            <Trash2 className="h-4 w-4 text-destructive" />
                                                                        </Button>
                                                                    </div>
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                        <TableRow className="bg-muted/30">
                                                            <TableCell></TableCell>
                                                            <TableCell colSpan={2} className="pl-12">
                                                                <Button
                                                                    variant="secondary"
                                                                    size="sm"
                                                                    onClick={() => handleAddUser(item.whname)}
                                                                >
                                                                    <Plus className="h-4 w-4 mr-2" />
                                                                    Add New User
                                                                </Button>
                                                            </TableCell>
                                                            <TableCell></TableCell>
                                                        </TableRow>
                                                    </>
                                                )}
                                            </React.Fragment>
                                        );
                                    })
                                ) : (
                                    !loading && (
                                        <TableRow>
                                            <TableCell colSpan={4} className="text-center text-muted-foreground">
                                                No users found
                                            </TableCell>
                                        </TableRow>
                                    )
                                )}
                            </TableBody>
                        </Table>
                    </div>

                    {totalPages > 1 && (
                        <div className="flex items-center justify-between px-2">
                            <div className="flex items-center gap-2">
                                <span className="text-sm text-muted-foreground">Jump to</span>
                                <Select value={String(currentPage)} onValueChange={(val) => handlePageChange(Number(val))}>
                                    <SelectTrigger className="w-20 h-8 text-sm">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                                            <SelectItem key={page} value={String(page)}>{page}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="flex items-center gap-2">
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => handlePageChange(1)}
                                    disabled={currentPage === 1}
                                >
                                    First
                                </Button>
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                >
                                    Previous
                                </Button>

                                <div className="flex gap-1">
                                    {pageNumbers.map(page => (
                                        <Button
                                            key={page}
                                            variant={page === currentPage ? "default" : "outline"}
                                            size="sm"
                                            onClick={() => handlePageChange(page)}
                                            className="min-w-[2.5rem]"
                                        >
                                            {page}
                                        </Button>
                                    ))}
                                </div>

                                <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                >
                                    Next
                                </Button>
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => handlePageChange(totalPages)}
                                    disabled={currentPage === totalPages}
                                >
                                    Last
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
                </>
                )}

                {deletePopup && (
                    <ConfirmDeleteAdminPopup
                        handleCondition={removeDashboardAccount}
                        DeleteSection={true}
                        titleText="Are you sure want to delete this dashboard?"
                        closePopup={() => setDeletePopup(false)}
                    />
                )}

                {addUserPopup && (
                    <AddUserPopup
                        titleText="Add User"
                        handleDashboard={handleAddUserSubmit}
                        closePopup={() => setAddUserPopup(false)}
                    />
                )}

                {createDashboardPopup && (
                    <ConfirmDeleteAdminPopup
                        handleConfirmCreate={handleConfirmCreateDashboard}
                        currentWholesalerName={wholesalerName}
                        titleText="Click on confirm to add a dashboard"
                        CloseCreatPopup={() => setCreateDashboardPopup(false)}
                    />
                )}

                {resendPasswordPopup && (
                    <ConfirmDeleteAdminPopup
                        ResendPasswordSection={true}
                        handleSubmit={handleSubmit}
                        titleText="Change Password"
                        closeResendPopup={() => setResendPasswordPopup(false)}
                        policyContext={adminResendPasswordPolicyContext}
                    />
                )}
            </DialogContent>
        </Dialog>
    );
};

export default ManageUserAdmin;
